import { useGetJwtUtente } from '@framework/api-client';
import {
  AuthError,
  ClientConfigLoader,
  getNetworkErrorMessage,
} from '@framework/auth';
import { LoaderSpin } from '@framework/ui';
import { Alert, message } from 'antd';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import {
  AuthActions,
  IRootAuthState,
  useAuth,
  useCurrentUser,
} from '../state/AuthSlice';

export interface IAuthCurrentLoaderProps {
  children: React.ReactNode;
}

const SkipHttpStatuses = [400, 401, 403];

/**
 * Caricamento dell'utente corrente. Serve anche per controllare come side
 * effect se l'utente è loggato, in quanto i token sono salvati su
 * cookie HTTP-Only e non sono quindi accessibili da JS.
 */
export function AuthCurrentLoader(props: IAuthCurrentLoaderProps) {
  const dispatch = useDispatch();
  const store = useStore<IRootAuthState>();
  const { isLogged, isPrelogged, fetchCount } = useAuth();
  const user = useCurrentUser();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null as any);
  const [errorType, setErrorType] = useState('error' as 'error' | 'info');

  const session = useGetJwtUtente({
    query: {
      enabled: false,
    },
  });

  useEffect(() => {
    let isMounted = true;

    const fetchCurrent = async () => {
      console.log('[AuthCurrentLoader]', { isLogged }); // prettier-ignore

      // Attualmente non supportiamo il refresh del token in questa
      // fase del caricamento dell'utente.
      const { accessToken, refreshToken } = store.getState().auth;
      if (!accessToken && !refreshToken) {
        dispatch(AuthActions.logout());
        return;
      }

      try {
        setLoading(true);
        const result = await session.refetch({ throwOnError: true });
        console.log('[AuthCurrentLoader] Refreshed user'); // prettier-ignore
        dispatch(AuthActions.currentLoaded({ current: result.data! }));
      } catch (e: any) {
        // Gestiamo il caso in cui il componente è stato ricaricato
        if (!isMounted) return;

        if (
          (e?.response && SkipHttpStatuses.includes(e.response.status)) ||
          e instanceof AuthError
        ) {
          message.error(`La sessione non è valida, si prega di autenticarsi nuovamente.`); // prettier-ignore
          dispatch(AuthActions.logout());
        } else {
          setErrorType('error');
          setError(
            getNetworkErrorMessage(
              e,
              "Si è verificato un errore durante il caricamento dell'utente.",
            ),
          );
        }
      }
      setLoading(false);
    };

    // Carichiamo l'utente ogni volta che siamo (pre)loggati, anche in
    // re-selezione ("Cambia profilo"): così la lista delle PG è aggiornata al
    // momento della scelta e copriamo il refresh pagina (dove `user` è null).
    // `currentLoaded` tiene `isLogged=false` finché `reselectionRequested` è
    // true, quindi il refetch non fa rientrare nell'app da solo.
    if (isLogged || isPrelogged) {
      fetchCurrent();
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogged, isPrelogged, fetchCount]);

  // Per evitare caricamenti a vuoto, mostriamo il loader
  if ((isPrelogged && !user) || loading) return <LoaderSpin full />;

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message={"Impossibie caricare l'utente"}
        description={error}
      />
      // TODO Inserire NetworkAlertPage
      // <NetworkAlertPage message="Impossibile caricare l'utente" error={error} />
    );
  }

  return (
    <>
      <ClientConfigLoader />
      {props.children}
    </>
  );
}
